import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuFileUp, LuX } from 'react-icons/lu';
import { LABEL_CLS } from 'helpers/formStyles';
import { comparableFile } from '../compareFormat';

const MAX_BYTES = 80 * 1024 * 1024;

export const fileProblem = (file) => {
  if (!file) return '';
  if (/\.doc$/i.test(file.name)) return `${file.name}: a .doc file cannot be compared — open it in Word and save it as .docx.`;
  if (!comparableFile(file.name)) return `${file.name}: only .docx and .pdf files can be compared.`;
  if (file.size > MAX_BYTES) return `${file.name} is too large to compare (over 80 MB).`;
  return '';
};

// One labelled drop zone for a .docx / .pdf; the parent keeps the chosen File.
const FileDrop = ({ label, hint, file, onChange }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const clear = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <p className={`${LABEL_CLS} tw-m-0 tw-mb-1 tw-font-medium`}>{label}</p>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onChange(e.dataTransfer.files?.[0] || null); }}
        className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3 tw-rounded-xl tw-border-2 tw-border-dashed ${dragging ? 'tw-border-primary-400 tw-bg-primary-50' : 'tw-border-slate-200 tw-bg-slate-50'}`}>
        <LuFileUp size={20} className="tw-text-slate-400 tw-shrink-0" aria-hidden="true" />
        <div className="tw-flex-1 tw-min-w-0">
          {file ? (
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-[13px] tw-font-semibold tw-text-slate-700">
              <span className="tw-truncate" title={file.name}>{file.name}</span>
              <button type="button" onClick={clear} aria-label={`Remove ${file.name}`}
                className="tw-p-0.5 tw-rounded tw-bg-transparent tw-border-0 tw-text-slate-400 hover:tw-text-red-600 tw-cursor-pointer">
                <LuX size={14} />
              </button>
            </span>
          ) : (
            <span className="tw-block tw-text-[12px] tw-text-slate-500">{hint}</span>
          )}
          <label className="tw-text-[12px] tw-font-semibold tw-text-primary-700 tw-cursor-pointer hover:tw-underline">
            {file ? 'Choose another file' : 'Drop here or choose a file'}
            <input ref={inputRef} type="file" accept=".docx,.pdf" className="tw-sr-only" aria-label={label}
              onChange={(e) => onChange(e.target.files?.[0] || null)} />
          </label>
        </div>
      </div>
    </div>
  );
};

FileDrop.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  file: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

export default FileDrop;
